import _ from 'lodash'

import { Line } from 'vue-chartjs'
import { getExtremaByRange, getFlat, getBlocks } from '../../utils/charts'
import getMarkerColor from '../../utils/getMarkerColor'
import { normalizeRange } from '../../utils/biomarker'

const GRADIENT_SMOOTHING = 0.25

export default {
  extends: Line,
  props: {
    item: { type: Object, required: true },
    results: { type: Array, required: true },
    timerange: { type: Array, default: () => [] }
  },
  data() {
    return {
      options: {},
      data: {},
      defaultOptions: {}
    }
  },
  computed: {
    getRange() {
      const { type } = this.item.range
      if (type === 'universal' || type === 'gender') {
        return normalizeRange(
          type === 'universal'
            ? { ...this.item.range.universal }
            : { ...this.item.range.male }
        )
      } else {
        return null
      }
    },
    getExtrema() {
      return getExtremaByRange(this.getRange)
    },
    getChartArea() {
      return this.$data._chart
        ? this.$data._chart.chartArea
        : { left: 0, top: 0, right: 400, bottom: this.height || 40 }
    },
    getChartHeight() {
      // chart height
      if (this.$data._chart) {
        const { top, bottom } = this.$data._chart.chartArea
        return bottom - top
      } else if (this.$refs.canvas) {
        return this.$refs.canvas.getContext('2d').canvas.height
      } else {
        return this.height || 40
      }
    },
    getBlocks() {
      const [gMin, gMax] = this.getExtrema
      return getBlocks(this.getRange, 0).map(x => {
        const min = _.clamp(x.min, gMin, gMax)
        const max = _.clamp(x.max, gMin, gMax)

        const bgColors = {
          '#7ac943': '#f5fbf0', // normal
          '#fbb03b': '#fff9f0', // borderline
          '#ff1d25': '#ffedee' // deficient
        }

        return {
          ...x,
          min,
          max,
          bgColor: bgColors[x.color]
        }
      })
    },
    getChartPointsColor() {
      const [min, max] = this.getExtrema
      return this.results.map(x =>
        getMarkerColor(_.clamp(x.quantity, min, max), this.getRange)
      )
    },
    getChartData() {
      const [min, max] = this.getExtrema
      return this.results.map(x => {
        const date = new Date(x.date)
        return {
          x: this.$moment(date).toISOString(),
          y: _.clamp(x.quantity, min, max)
        }
      })
    },
    getGradientStroke() {
      const ctx = this.$refs.canvas.getContext('2d')
      const { top, bottom } = this.getChartArea
      const [gMin] = this.getExtrema
      const flat = getFlat(this.getExtrema)
      const height = this.getChartHeight

      const gradientStroke = ctx.createLinearGradient(0, 0, 0, bottom)
      this.getBlocks.forEach(({ min, max, color }) => {
        const multiplier = height / (height + top)
        const start = 1 - ((min - gMin) / flat) * multiplier
        const end = 1 - ((max - gMin) / flat) * multiplier
        const padding = getFlat([start, end]) * GRADIENT_SMOOTHING
        gradientStroke.addColorStop(start + padding, color)
        gradientStroke.addColorStop(end - padding, color)
      })
      this.getChartData.forEach(({ y }, index) => {
        const color = this.getChartPointsColor[index]
        gradientStroke.addColorStop(1 - (y - gMin) / flat, color)
      })

      return gradientStroke
    },
    getData() {
      return {
        labels: this.timerange,
        datasets: [
          {
            borderColor: this.getGradientStroke,
            // pointBackgroundColor: this.getGradientStroke,
            pointBackgroundColor: this.getChartPointsColor,
            pointRadius: 3.5,
            pointBorderWidth: 0.01,
            pointHoverRadius: 4,
            pointHoverBorderWidth: 0.01,
            fill: false,
            borderWidth: 2,
            data: this.getChartData
          }
        ]
      }
    }
  },
  mounted() {
    this.renderBackground()

    const data = this.getData
    this.data = data

    const [min, max] = this.getExtrema
    const defaultOptions = {
      tooltips: {
        callbacks: {
          title: () => {
            return ''
          },
          label: item => {
            return item.yLabel
          }
        },
        cornerRadius: 3,
        backgroundColor: '#004953'
      },
      legend: false,
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          bottom: -10,
          // there is no easy way to solve chart.js points overflow
          // the best way to deal with this it is padding
          left: 2,
          right: 12
        }
      },
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: false,
              min,
              max
            }
          }
        ],
        xAxes: [
          {
            type: 'time'
          }
        ]
      }
    }
    this.defaultOptions = defaultOptions

    this.renderChart(data, _.merge(defaultOptions, this.options))
  },
  methods: {
    scaleValue(value, height = this.getChartHeight) {
      const [gMin] = this.getExtrema
      const flat = getFlat(this.getExtrema)
      return ((value - gMin) / flat) * height
    },
    renderBackground() {
      this.addPlugin({
        id: 'background',
        beforeDraw: ({ chart, chartArea }) => {
          const ctx = chart.ctx
          const chartWidth = chartArea.right - chartArea.left
          const chartHeight = chartArea.bottom - chartArea.top

          this.getBlocks.forEach(({ min, max, bgColor }) => {
            const blockHeight =
              this.scaleValue(max, chartHeight) -
              this.scaleValue(min, chartHeight)

            ctx.fillStyle = bgColor
            ctx.fillRect(
              chartArea.left,
              chartHeight - this.scaleValue(min, chartHeight) + chartArea.top,
              chartWidth,
              -blockHeight
            )
          })
          ctx.save()
        }
      })
    }
  },
  watch: {
    getChartHeight() {
      if (this.$data._chart) {
        this.renderChart(
          this.getData,
          _.merge(this.defaultOptions, this.options)
        )
      }
    }
  }
}
